import moment from "moment";

const HelperService = {
  formatDate(date: string) {
    if (date) {
      return moment(date).format("DD-MM-YYYY");
    }
  },
  truncate(str: string, size: number) {
    return str.length > size ? str?.substring(0, size) + "..." : str;
  },
  removeDecimal(value: any) {
    return value ? Math.floor(value) : 0;
  },
  validateEmail(email: any) {
    const regex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  },
  // Strict email validation matching the public registration form
  // (frontend HelperService.isValidEmail) so an email accepted by an admin
  // create form is also accepted at login/registration and vice-versa.
  isValidEmail(email: any) {
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|in|net|org|co\.in)$/i;
    return regex.test(email);
  },
  // URL / LinkedIn / social validators mirrored from the registration form so
  // admin create forms enforce the same formats.
  checkWebUrl(url: string) {
    const urlPattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/[\w.-]*)*\/?$/i;
    return urlPattern.test(url);
  },
  checkLinkedinUrl(url: string) {
    if (!url || !url.trim()) return false;
    const linkedinPattern =
      /^(https?:\/\/)?([a-z0-9-]+\.)?linkedin\.com\/(in|company|school|pub|profile)\/[\w\-./?%&=@#]+$/i;
    return linkedinPattern.test(url.trim());
  },
  isValidSocialUrl(url: string): boolean {
    const socialUrlPattern =
      /^(https?:\/\/)?(www\.)?(facebook|twitter|instagram|linkedin|youtube|tiktok|github|reddit|pinterest|snapchat|twitch|threads)\.com(\/[\w\-./?%&=]*)?$/i;
    const websiteUrlPattern =
      /^(https?:\/\/)?([\w-]+\.)+[\w]{2,}(\/[\w\-./?%&=]*)?$/i;
    return socialUrlPattern.test(url) || websiteUrlPattern.test(url);
  },
  // Lightweight mobile-number check (admin has no libphonenumber). Registration
  // validates via libphonenumber; here we accept an optional number but, when
  // present, require a plausible 6-15 digit national number.
  isMobileNumberValid(mobileNumber: any) {
    if (!mobileNumber) return true; // optional
    const digits = String(mobileNumber).replace(/\D/g, "");
    return digits.length >= 6 && digits.length <= 15;
  },
  isValidPassword(password: string) {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    if (passwordRegex.test(password)) return true;
    else return false;
  },
  removeUnderScore(str: any) {
    if (!str) return "-";
    if ((str !== "" || str !== undefined) && str.includes("_")) {
      return this.titleCase(str.replace(/_/g, " "));
    } else {
      return this.titleCase(str);
    }
  },
  removeDialCodeFromMobile(mobileNumber: any, dialCode: any) {
    if (
      typeof mobileNumber === "string" &&
      typeof dialCode === "string" &&
      dialCode.length > 0
    ) {
      if (mobileNumber.startsWith(dialCode)) {
        return mobileNumber.slice(dialCode.length);
      }
    }
    return mobileNumber;
  },
  titleCase(str: any) {
    if (str !== "") {
      return str
        .toLowerCase()
        .split(" ")
        .map((word: any) => {
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(" ");
    } else {
      return str;
    }
  },
  getFormattedDateWithTime(dt: any) {
    if (!dt || dt === "0000-00-00") return "-";
    const str = String(dt).trim();
    const m = !str.endsWith("Z") && !str.includes("+")
      ? moment.utc(str).local()
      : moment(str).local();
    return m.isValid() ? m.format("DD-MM-YYYY hh:mm A") : "-";
  },
  getDivHeight(id: any) {
    let div = document.getElementById(id);
    if (div) {
      return div.offsetHeight;
    }
  },
  toProperCase(str: string) {
    if (!str) {
      return "";
    }
    const pattern = /_/gi;
    const replacement = " ";
    const result = str.replaceAll(pattern, replacement);

    return result.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  },
  decideBadgeClass(status: string) {
    if (status === "ACTIVE") return "badge badge-green";
    else if (status === "INACTIVE") return "badge badge-danger";
    else if (status === "In_Active") return "badge badge-danger";
    else if (status === "PUBLISHED") return "badge badge-green";
    else if (status === "Published") return "badge badge-green";
    else if (status === "UNPUBLISHED") return "badge badge-danger";
    else if (status === "APPROVED") return "success";
    else if (status === "REJECTED") return "danger";
    else if (status === "PENDING") return "warning";
  },
  formatDevice(ua: string) {
    if (!ua) return "-";

    if (ua.includes("Chrome")) return "Chrome Browser";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Windows")) return "Windows Device";
    if (ua.includes("Android")) return "Android Device";
    if (ua.includes("iPhone")) return "iPhone";

    return "Unknown Device";
  },
  getFormatedDateByDMYTime(d: any) {
    if (!d || d === "0000-00-00") return "NA";
    const str = String(d).trim();
    const m = !str.endsWith("Z") && !str.includes("+")
      ? moment.utc(str).local()
      : moment(str).local();
    return m.isValid() ? m.format("DD-MMM-YYYY hh:mm A") : "NA";
  },
  formatAmountIntl(amount1: string | number = 0): string {
    if (amount1 === null || amount1 === undefined) return "";

    const amount = typeof amount1 === "number" ? amount1 : parseFloat(amount1);
    if (isNaN(amount)) return "";

    if (amount >= 1_000_000_000_000) {
      return `${(amount / 1_000_000_000_000).toFixed(2)} T`;
    } else if (amount >= 1_000_000_000) {
      return `${(amount / 1_000_000_000).toFixed(2)} B`;
    } else if (amount >= 1_000_000) {
      return `${(amount / 1_000_000).toFixed(2)} M`;
    } else if (amount >= 1_000) {
      return `${(amount / 1_000).toFixed(2)} K`;
    } else {
      return amount.toFixed(2);
    }
  },
  isRmInDevelopment(): boolean {
    const val =
      process.env.REACT_APP_IS_RM_IN_DEVELOPMENT ||
      process.env.IS_RM_IN_DEVELOPMENT ||
      "";
    return val.toString().trim().toUpperCase() === "TRUE";
  },
};

export default HelperService;

